// The "staging table" model for the File Manager's upload modal (#395).
//
// After files are picked, every file gets a destination folder. A GLOBAL
// destination sets them all at once, and any single row can override it:
//
//   state = { global, rows: [{ id, file, dest, overridden }] }
//
// `dest` / `global` are a folder id, or null for the root ("My Documents").
//
// Override rule — deliberately simple and symmetric:
//   - changing the global destination moves every row that is NOT overridden;
//   - a row is "overridden" exactly when its destination differs from the
//     global one, so picking the global value on a row hands it back to the
//     global automatically (no stale override left behind), and `resetRow`
//     does the same explicitly;
//   - a row keeps its own choice when the global destination changes later.

let seq = 0;
const nextId = () => `stage-${Date.now()}-${++seq}`;
const norm = (v) => (v === undefined || v === '' ? null : v);

export function createStaging(defaultDest = null) {
    return { global: norm(defaultDest), rows: [] };
}

export function addFiles(state, files = []) {
    const rows = files.map((file) => ({ id: nextId(), file, dest: state.global, overridden: false }));
    return { ...state, rows: [...state.rows, ...rows] };
}

export function removeRow(state, rowId) {
    return { ...state, rows: state.rows.filter((r) => r.id !== rowId) };
}

export function clearRows(state) {
    return { ...state, rows: [] };
}

export function setGlobal(state, dest) {
    const global = norm(dest);
    return {
        global,
        rows: state.rows.map((r) => (r.overridden ? r : { ...r, dest: global })),
    };
}

export function setRowDest(state, rowId, dest) {
    const value = norm(dest);
    return {
        ...state,
        rows: state.rows.map((r) => (
            r.id === rowId ? { ...r, dest: value, overridden: value !== state.global } : r
        )),
    };
}

export function resetRow(state, rowId) {
    return setRowDest(state, rowId, state.global);
}

/** The upload plan: files grouped by destination, in first-seen order. */
export function toUploadPlan(state) {
    const groups = new Map();
    for (const r of state.rows) {
        if (!groups.has(r.dest)) groups.set(r.dest, []);
        groups.get(r.dest).push(r.file);
    }
    return Array.from(groups, ([dest, files]) => ({ dest, files }));
}

/**
 * The options a destination selector offers, from the flat tree rows.
 * Protected /Financials folders are not offered (receipts are routed there by
 * book + direction) EXCEPT the folder the user is already browsing, which stays
 * selectable so "default = the folder I'm in" keeps working exactly as it did
 * before this modal had selectors.
 */
export function destinationOptions(treeFolders = [], { currentFolderId = null, rootLabel = 'My Documents', currentFolderLabel = '…' } = {}) {
    const options = [{ value: null, label: rootLabel, depth: 0 }];
    let sawCurrent = !currentFolderId;
    for (const f of treeFolders) {
        const isCurrent = f.id === currentFolderId;
        if (f.is_protected && !isCurrent) continue;
        if (isCurrent) sawCurrent = true;
        options.push({ value: f.id, label: f.path || f.name, depth: f.depth || 0 });
    }
    // Tree not loaded (or the open folder is missing from it): keep the open
    // folder selectable so the default destination is always representable.
    if (!sawCurrent) options.push({ value: currentFolderId, label: currentFolderLabel, depth: 1 });
    return options;
}
